import { ApiError } from './errors';

/**
 * Codes the server puts on refusals written for a customer on a public page.
 * Mirrors `PUBLIC_REFUSAL_CODES` in
 * `apps/operations/src/common/errors/public-refusal-codes.ts`; keep the two
 * lists in step.
 */
const PUBLIC_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'REPAIR_LINK_UNAVAILABLE',
  'PAYMENT_PROVIDER_MISMATCH',
  'REPAIR_IN_PROGRESS',
  'CARD_SETUP_UNAVAILABLE',
]);

export const TOO_MANY_ATTEMPTS_MESSAGE = 'Too many attempts. Please wait a minute and try again.';

/**
 * The server's message for a refusal written for the customer, or null.
 *
 * A public page must not echo any 4xx it gets: the same services answer staff
 * routes with Stripe account, tenant and record-id detail, and validation or
 * throttling failures carry framework text. Only a refusal the server marked
 * with a public code is safe to show.
 */
export function publicRefusalMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  // `ApiError.code` is derived from the status; the server's code is in the body.
  const code = (error.data as { code?: unknown } | null | undefined)?.code;
  if (typeof code !== 'string' || !PUBLIC_REFUSAL_CODES.has(code)) return null;
  return error.message.trim() || null;
}

/** The throttler turned the request away; the same request works after a wait. */
export function isTooManyAttempts(error: unknown): boolean {
  return error instanceof ApiError && error.status === 429;
}

/**
 * The server refused the link itself: invalid, expired, used, or no longer
 * needed. Only this code says the link is closed. Other 4xx answers from the
 * same routes (a processor mismatch, an uncoded gateway 403/404) arrive while
 * the link is still open.
 */
export function isRepairLinkUnavailable(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.data as { code?: unknown } | null | undefined)?.code === 'REPAIR_LINK_UNAVAILABLE'
  );
}

/**
 * Another completion holds the repair's single-use claim. Like a 429 this says
 * nothing about the card, so the same request can be retried once it lets go.
 */
export function isRepairInProgress(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.data as { code?: unknown } | null | undefined)?.code === 'REPAIR_IN_PROGRESS'
  );
}

/**
 * The error a public page's add-method form should show for a failed save. The
 * shared setup forms render a rejected `onComplete`'s message as-is, and they
 * are used by staff pages too, so a public page rewrites the rejection before
 * it gets there: wait copy on a 429, the server's text only for a coded public
 * refusal, `fallback` otherwise.
 */
export function publicSaveError(error: unknown, fallback: string): Error {
  return new Error(
    isTooManyAttempts(error)
      ? TOO_MANY_ATTEMPTS_MESSAGE
      : (publicRefusalMessage(error) ?? fallback),
  );
}
